import { useState, useEffect, useRef, useMemo } from 'react';
import { calculateTotalPageCount } from '../utils/pdfToImage';

/**
 * ファイル配列の合計ページ数を非同期で計算するカスタムフック
 * PDFファイルはページ数分、画像ファイルは1ページとしてカウント
 * @param files ファイル配列
 * @returns totalPageCount: 合計ページ数, isCalculating: 計算中フラグ
 */
export const useTotalPageCount = (
  files: File[] | undefined,
): { totalPageCount: number; isCalculating: boolean } => {
  const [totalPageCount, setTotalPageCount] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const calculationIdRef = useRef<number>(0);
  const filesRef = useRef<File[] | undefined>(files);
  filesRef.current = files;

  // react-hook-formのwatchは同じ配列参照を返す場合があるため、
  // ファイル名とサイズからキーを生成して変更を検知する
  const filesKey = useMemo(() => {
    if (!files || files.length === 0) return '';
    return files.map(f => `${f.name}_${f.size}`).join(',');
  }, [files]);

  useEffect(() => {
    const currentFiles = filesRef.current;
    if (!currentFiles || currentFiles.length === 0) {
      setTotalPageCount(0);
      setIsCalculating(false);
      return;
    }

    calculationIdRef.current += 1;
    const currentId = calculationIdRef.current;

    setIsCalculating(true);

    const calculate = async (): Promise<void> => {
      const count = await calculateTotalPageCount(currentFiles);
      if (currentId === calculationIdRef.current) {
        setTotalPageCount(count);
        setIsCalculating(false);
      }
    };

    calculate();
  }, [filesKey]);

  return { totalPageCount, isCalculating };
};
